import React, { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import EmailThread from "../../leadsComponents/EmailThread";
import ConnectGmailButton from "../../leadsComponents/ConnectGmailButton";
import { INQUIRY_TYPES, TYPE_TONE } from "../constants";
import {
  classNames,
  firstResponseMinutes,
  formatDateTime,
  formatDuration,
  formatRelative,
  statusOf,
  urgencyFor,
  urgencyTone,
} from "./utils";
import InquiryReplyComposer from "./InquiryReplyComposer";

function Section({ title, children, action }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card">
      <header className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          {title}
        </h3>
        {action}
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function StatusBadge({ inquiry }) {
  const status = statusOf(inquiry);
  const map = {
    new: { label: "New", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    replied: { label: "Replied", cls: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
    closed: { label: "Closed", cls: "bg-zinc-100 text-zinc-600 ring-zinc-200" },
  };
  const meta = map[status];
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        meta.cls
      )}
    >
      {meta.label}
    </span>
  );
}

function UrgencyChip({ inquiry }) {
  const id = urgencyFor(inquiry);
  if (!id) return null;
  const tone = urgencyTone(id);
  const label = { overdue: "Overdue", stale: "Stale", fresh: "Fresh", new: "Just now" }[id];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        tone.chip
      )}
    >
      <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
      {label}
    </span>
  );
}

export default function InquiryDetailDrawer({
  inquiry,
  loading,
  adminInfo,
  gmailConnected,
  onConnectGmail,
  onClose,
  onPatchInquiry,
  onSendReply,
  onNext,
  onPrevious,
}) {
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    setNotesDraft(inquiry?.notes || "");
    setSendError("");
  }, [inquiry?._id, inquiry?.notes]);

  const responderName = `${adminInfo?.firstName || ""} ${adminInfo?.lastName || ""}`.trim();
  const tone = TYPE_TONE[inquiry?.type || "Other"] || TYPE_TONE.Other;
  const firstRespMin = useMemo(() => firstResponseMinutes(inquiry), [inquiry]);

  if (!inquiry) return null;

  const saveNotes = async () => {
    if (!inquiry._id) return;
    setSavingNotes(true);
    try {
      await onPatchInquiry?.(inquiry._id, { notes: notesDraft });
    } finally {
      setSavingNotes(false);
    }
  };

  const toggleClosed = async () => {
    if (!inquiry._id) return;
    await onPatchInquiry?.(inquiry._id, { closed: !inquiry.closed });
  };

  const markReplied = async () => {
    if (!inquiry._id) return;
    await onPatchInquiry?.(inquiry._id, { replied: true });
  };

  const changeType = async (next) => {
    if (!inquiry._id) return;
    await onPatchInquiry?.(inquiry._id, { type: next });
  };

  const handleSendReply = async (payload) => {
    if (!inquiry._id) return;
    setSending(true);
    setSendError("");
    try {
      await onSendReply?.({
        id: inquiry._id,
        responder: responderName,
        ...payload,
      });
    } catch (err) {
      setSendError(err?.response?.data?.error || err?.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <aside className="flex h-full w-[540px] shrink-0 flex-col border-l border-zinc-200 bg-white">
      <header className="flex items-start justify-between border-b border-zinc-200 px-4 py-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-base font-semibold text-zinc-900">
              {inquiry.name || "Unnamed"}
            </h2>
            <StatusBadge inquiry={inquiry} />
            <UrgencyChip inquiry={inquiry} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500">
            <span className="truncate">{inquiry.email || "no email"}</span>
            {inquiry.phone ? (
              <>
                <span className="text-zinc-300">·</span>
                <span>{inquiry.phone}</span>
              </>
            ) : null}
            <span className="text-zinc-300">·</span>
            <span title={new Date(inquiry.createdAt).toString()}>
              received {formatRelative(inquiry.createdAt)}
            </span>
            {firstRespMin != null ? (
              <>
                <span className="text-zinc-300">·</span>
                <span>1st reply in {formatDuration(firstRespMin)}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrevious}
            title="Previous (k)"
            className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={onNext}
            title="Next (j)"
            className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close (Esc)"
            className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="border-b border-zinc-200 bg-zinc-50/60 px-4 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1",
                tone.chip
              )}
            >
              <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
              {inquiry.type || "Other"}
            </span>
            <select
              value={inquiry.type || "Other"}
              onChange={(e) => changeType(e.target.value)}
              className="rounded-md border border-zinc-200 bg-white px-1.5 py-1 text-[11px] text-zinc-700 focus:border-indigo-500 focus:outline-none"
            >
              {INQUIRY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-1">
            {!inquiry.replied && !inquiry.closed ? (
              <button
                type="button"
                onClick={markReplied}
                className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
                title="Mark as replied without sending"
              >
                Mark replied
              </button>
            ) : null}
            <button
              type="button"
              onClick={toggleClosed}
              className={classNames(
                "rounded-md border px-2 py-1 text-[11px] font-medium",
                inquiry.closed
                  ? "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                  : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
              )}
            >
              {inquiry.closed ? "Reopen" : "Close inquiry"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="space-y-3 p-4">
          <Section
            title="Original message"
            action={
              inquiry.subject ? (
                <span className="text-[11px] font-medium text-zinc-700">
                  {inquiry.subject}
                </span>
              ) : null
            }
          >
            <div className="text-[11px] text-zinc-500">
              Received {formatDateTime(inquiry.createdAt)}
            </div>
            <div className="mt-2 whitespace-pre-wrap rounded-lg bg-zinc-50 p-3 text-sm text-zinc-800">
              {inquiry.message || (
                <span className="italic text-zinc-400">(no message body)</span>
              )}
            </div>
          </Section>

          {inquiry.threadId || (inquiry.messages || []).length ? (
            <Section title="Gmail thread">
              <EmailThread
                messages={inquiry.messages || []}
                inquiryId={inquiry._id}
                threadId={inquiry.threadId}
                key={inquiry.threadId || inquiry._id}
              />
            </Section>
          ) : null}

          <Section title="Reply">
            {gmailConnected ? (
              <InquiryReplyComposer
                inquiry={inquiry}
                adminInfo={adminInfo}
                sending={sending}
                sendError={sendError}
                onSend={handleSendReply}
              />
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-zinc-600">
                  Connect Gmail to send a reply from your mailbox.
                </p>
                {onConnectGmail ? (
                  <button
                    onClick={onConnectGmail}
                    className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                  >
                    Connect Gmail
                  </button>
                ) : (
                  <ConnectGmailButton />
                )}
              </div>
            )}
          </Section>

          <Section
            title="Internal notes"
            action={
              <button
                type="button"
                onClick={saveNotes}
                disabled={savingNotes}
                className="rounded-md bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-indigo-500 disabled:bg-zinc-300"
              >
                {savingNotes ? "Saving…" : "Save"}
              </button>
            }
          >
            <textarea
              rows={3}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Private context for your team"
              className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </Section>

          <Section
            title={`Response history (${(inquiry.responses || []).length})`}
          >
            {(inquiry.responses || []).length === 0 ? (
              <p className="text-xs text-zinc-500">No replies logged yet.</p>
            ) : (
              <ul className="space-y-2">
                {inquiry.responses
                  .slice()
                  .reverse()
                  .map((resp, idx) => (
                    <li
                      key={`${resp.respondedAt || idx}-${idx}`}
                      className="rounded-lg border border-zinc-200 bg-zinc-50 p-2.5"
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-zinc-800">
                          {resp.responder || "Unknown"}
                        </span>
                        <span className="text-zinc-500">
                          {resp.respondedAt
                            ? format(new Date(resp.respondedAt), "PPpp")
                            : "—"}
                        </span>
                      </div>
                      <div className="mt-1 whitespace-pre-wrap text-sm text-zinc-700">
                        {resp.message}
                      </div>
                      {resp.templateUsed ? (
                        <div className="mt-1 text-[11px] text-zinc-400">
                          Template: {resp.templateUsed}
                        </div>
                      ) : null}
                    </li>
                  ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </aside>
  );
}
